import { MobileDatePicker, MobileDateTimePicker } from '@mui/lab'
import { Grid } from '@mui/material'
import type { ButtonProps } from '@mui/material'
import { format, startOfDay } from 'date-fns'
import { forwardRef, Ref } from 'react'
import { useTranslation } from 'react-i18next'
import { Noop } from 'react-hook-form'
import { round, roundDown, roundUp } from 'shared/utils'
import {
  createDatePickerDialogProps,
  DatePickerClearButtonStyled,
  PickerPlaceholderTextField,
} from './DatePicker.style'
const DATETIME_FORMAT = 'dd.MM.yyyy HH:mm'
const DATETIME_INPUT_MASK = '__.__.____ __:__'
const DATE_FORMAT = 'dd.MM.yyyy'
const DATE_INPUT_MASK = '__.__.____'

const createShouldDisablePickerMinutes = (minutesStep: number) => {
  return (timeValue: number, clockType: string) =>
    clockType === 'minutes' && timeValue % minutesStep !== 0
}

export enum RoundMode {
  Up,
  Down,
  Nearest,
  Daily,
}

const DatePicker = forwardRef(
  (
    {
      onChange,
      onClose,
      value,
      onBlur,
      label,
      errorMessage,
      revalidateFunc,
      showTime = false,
      showTimezoneInLabel = true,
      disabled = false,
      step,
      minDate,
      maxDate,
      useDateFormat,
      showTimezoneLabel = true,
      readOnlyInput = false,
      externalLabel = false,
      inputId,
      showTimeZoneInInput = false,
      specificInputLabel,
      displayTimeZone,
    }: {
      onChange: (val: Date | null) => any
      onClose?: () => void
      value: Date | null
      onBlur: Noop
      label?: string
      revalidateFunc: () => Promise<any>
      errorMessage?: string
      showTime?: boolean
      showTimezoneInLabel?: boolean
      disabled?: boolean
      step?: {
        minutesStep: number
        roundMode: RoundMode
      }
      minDate?: Date | null | undefined
      maxDate?: Date | null | undefined
      useDateFormat?: boolean
      showTimezoneLabel?: boolean
      readOnlyInput?: boolean
      externalLabel?: boolean
      inputId?: string
      showTimeZoneInInput?: boolean
      specificInputLabel?: string
      displayTimeZone?: string
    },
    ref: Ref<HTMLInputElement>
  ) => {
    const { t } = useTranslation()
    const appendTimezoneToLabel = showTimezoneLabel && showTimezoneInLabel
    const timeZoneForInput = displayTimeZone?.trim() ?? ''

    const timezoneSuffix = appendTimezoneToLabel
      ? ` (${format(value ?? new Date(), 'O')})`
      : ''

    const pickerLabel =
      externalLabel || !label ? undefined : `${label}${timezoneSuffix}`

    const pickerInputLabel = specificInputLabel
      ? `${specificInputLabel}${timezoneSuffix}`
      : pickerLabel

    const resolveDateTimeInputProps = () => {
      if (!showTime || !showTimeZoneInInput || !timeZoneForInput) {
        return {
          placeholder: undefined as string | undefined,
          inputValue: undefined,
          inputValueIsPlaceholder: false,
        }
      }

      const placeholder = `(${timeZoneForInput})`

      if (value) {
        return {
          placeholder,
          inputValue: `${format(value, DATETIME_FORMAT)} (${timeZoneForInput})`,
          inputValueIsPlaceholder: false,
        }
      }

      return {
        placeholder,
        inputValue: placeholder,
        inputValueIsPlaceholder: true,
      }
    }

    const resolveDateInputProps = () => {
      if (!useDateFormat || !showTimeZoneInInput || !timeZoneForInput) {
        return {
          placeholder: undefined as string | undefined,
          inputValue: undefined,
          inputValueIsPlaceholder: false,
        }
      }

      const placeholder = `(${timeZoneForInput})`

      if (value) {
        return {
          placeholder,
          inputValue: `${format(value, DATE_FORMAT)} (${timeZoneForInput})`,
          inputValueIsPlaceholder: false,
        }
      }

      return {
        placeholder,
        inputValue: placeholder,
        inputValueIsPlaceholder: true,
      }
    }

    const pickerDialogProps = createDatePickerDialogProps(
      Boolean(step?.minutesStep)
    )

    return showTime ? (
      <MobileDateTimePicker
        DialogProps={pickerDialogProps}
        cancelText={t('cancel')}
        okText={t('ok')}
        onClose={() => {
          if (onClose) onClose()
        }}
        onChange={async date => {
          if (step) {
            if (date) {
              const stepValue = step.minutesStep * 60 * 1000
              if (date.getTime() % stepValue !== 0) {
                switch (step.roundMode) {
                  case RoundMode.Down:
                    onChange(roundDown(stepValue, date))
                    break
                  case RoundMode.Up:
                    onChange(roundUp(stepValue, date))
                    break
                  case RoundMode.Nearest:
                    onChange(round(stepValue, date))
                    break
                }
              } else onChange(date)
            } else onChange(null)
          } else onChange(date ? date : null)

          await revalidateFunc()
        }}
        minutesStep={step?.minutesStep}
        shouldDisableTime={
          step?.minutesStep
            ? createShouldDisablePickerMinutes(step.minutesStep)
            : undefined
        }
        value={value}
        inputRef={ref}
        label={pickerLabel}
        inputFormat={DATETIME_FORMAT}
        mask={DATETIME_INPUT_MASK}
        disabled={disabled}
        minDate={minDate}
        maxDate={maxDate}
        renderInput={props => {
          const {
            placeholder: tzPlaceholder,
            inputValue,
            inputValueIsPlaceholder,
          } = resolveDateTimeInputProps()

          return (
            <PickerPlaceholderTextField
              onBlur={onBlur}
              autoComplete="off"
              fullWidth
              {...props}
              id={inputId ?? props.id}
              placeholder={tzPlaceholder}
              sx={props.sx}
              showPlaceholderStyle={inputValueIsPlaceholder}
              inputProps={{
                ...props.inputProps,
                readOnly: showTimeZoneInInput || readOnlyInput,
                ...(inputValue !== undefined ? { value: inputValue } : {}),
              }}
              error={!!errorMessage}
              helperText={errorMessage}
              label={pickerInputLabel}
            />
          )
        }}
      />
    ) : (
      <MobileDatePicker
        DialogProps={pickerDialogProps}
        cancelText={t('cancel')}
        okText={t('ok')}
        onClose={() => {
          if (onClose) onClose()
        }}
        onChange={async date => {
          onChange(date ? startOfDay(date) : null)
          await revalidateFunc()
        }}
        value={value}
        inputRef={ref}
        label={pickerLabel}
        inputFormat={useDateFormat ? DATE_FORMAT : DATETIME_FORMAT}
        mask={useDateFormat ? DATE_INPUT_MASK : DATETIME_INPUT_MASK}
        disabled={disabled}
        minDate={minDate}
        maxDate={maxDate}
        renderInput={props => {
          const {
            placeholder: tzPlaceholder,
            inputValue,
            inputValueIsPlaceholder,
          } = resolveDateInputProps()

          return (
            <PickerPlaceholderTextField
              onBlur={onBlur}
              autoComplete="off"
              fullWidth
              {...props}
              id={inputId ?? props.id}
              placeholder={tzPlaceholder}
              sx={props.sx}
              showPlaceholderStyle={inputValueIsPlaceholder}
              inputProps={{
                ...props.inputProps,
                readOnly:
                  showTimeZoneInInput && useDateFormat ? true : readOnlyInput,
                ...(inputValue !== undefined ? { value: inputValue } : {}),
              }}
              error={!!errorMessage}
              helperText={errorMessage}
            />
          )
        }}
      />
    )
  }
)

interface DatePickerClearButtonProps extends ButtonProps {
  label: string
  onClick: () => Promise<any>
  error?: string
}

export const DatePickerClearButton = ({
  label,
  error,
  onClick,
  ...props
}: DatePickerClearButtonProps) => {
  return (
    <Grid item xs={12} md={2}>
      <DatePickerClearButtonStyled
        {...props}
        fullWidth={false}
        hasError={Boolean(error)}
        onClick={onClick}
      >
        {label}
      </DatePickerClearButtonStyled>
    </Grid>
  )
}

export default DatePicker
